import { queryAgg, writeAggQuery } from '@confluence/query-preloader-tools/entry-points/graphql.ts';
import { ssrPrepareErrorsClient } from '@confluence/ssr-utilities/entry-points/SSRPrepareErrorsClient.ts';

import type { BylineContentType } from '../utils/contentAri.ts';

import IncomingLinksSampleQuery, {
	type useIncomingLinksQuery$data,
	type useIncomingLinksQuery$variables,
} from '../hooks/__generated__/useIncomingLinksQuery.graphql.ts';
import {
	buildIncomingLinksSampleVariables,
	normalizeIncomingLinksSampleData,
	type IncomingLinksSampleResult,
	type IncomingLinksSampleUnavailableReason,
} from './incomingLinksSsrState.ts';

type IncomingLinksSampleCauseCategory = 'error' | 'graphql' | 'none' | 'non-error';

const unavailable = (reason: IncomingLinksSampleUnavailableReason): IncomingLinksSampleResult => ({
	status: 'unavailable',
	reason,
});

const reportUnavailable = (
	reason: Exclude<IncomingLinksSampleUnavailableReason, 'invalid-input'>,
	cause?: unknown,
) => {
	const causeCategory: IncomingLinksSampleCauseCategory =
		cause == null
			? 'none'
			: cause instanceof Error
				? cause.name === 'GraphQLError'
					? 'graphql'
					: 'error'
				: 'non-error';

	ssrPrepareErrorsClient.submitError(
		new Error(`Incoming links sample unavailable: ${reason}`, { cause }),
		{
			operation: 'preload-incoming-links-sample',
			reason,
			causeCategory,
		},
	);
};

export const preloadIncomingLinksSample = async ({
	cloudId,
	contentId,
	contentType,
}: {
	cloudId: string;
	contentId: string;
	contentType?: BylineContentType;
}): Promise<IncomingLinksSampleResult> => {
	const variables = buildIncomingLinksSampleVariables(cloudId, contentId, contentType);
	if (!variables) {
		return unavailable('invalid-input');
	}

	try {
		// No `no-cache`: the sample warms the AGG query cache so the client can read the same query
		// `cache-only` after SSR hydration, mirroring the outbound byline preload.
		const { data, errors, hasErrors } = await queryAgg<
			useIncomingLinksQuery$data,
			useIncomingLinksQuery$variables
		>({
			query: IncomingLinksSampleQuery,
			variables,
			context: {
				headers: {
					'X-Query-Context': `ari:cloud:platform::site/${cloudId}`,
				},
			},
		});

		if (hasErrors || errors?.length) {
			reportUnavailable('query-error', errors?.[0]);
			return unavailable('query-error');
		}

		const connection = data?.graphStore_cypherQueryV2;
		const pageInfo = connection?.pageInfo;
		if (connection && pageInfo?.hasNextPage === false && pageInfo.endCursor === undefined) {
			// Relay needs an explicit null to reuse a final page with an omitted cursor.
			try {
				writeAggQuery<useIncomingLinksQuery$data, useIncomingLinksQuery$variables>({
					query: IncomingLinksSampleQuery,
					variables,
					data: {
						...data,
						graphStore_cypherQueryV2: {
							...connection,
							pageInfo: { ...pageInfo, endCursor: null },
						},
					},
				});
			} catch (writeError) {
				ssrPrepareErrorsClient.submitError(
					new Error('Incoming links cursor patch failed', { cause: writeError }),
					{ operation: 'preload-incoming-links-sample', reason: 'cursor-patch-failed' },
				);
			}
		}

		const result = normalizeIncomingLinksSampleData(data);
		if (result.status === 'unavailable') {
			reportUnavailable('malformed-response');
		}
		return result;
	} catch (cause) {
		reportUnavailable('query-error', cause);
		return unavailable('query-error');
	}
};
